// src/components/ui/alert-dialog.tsx
import React, { useState } from 'react';
import { Button } from './Button'; // Adjust path if needed

interface AlertDialogProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface AlertDialogTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

interface AlertDialogContentProps {
  children: React.ReactNode;
  className?: string;
}

interface AlertDialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface AlertDialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface AlertDialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface AlertDialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

interface AlertDialogActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

interface AlertDialogCancelProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const AlertDialogContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
} | null>(null);

const AlertDialog: React.FC<AlertDialogProps> = ({ children, open: controlledOpen, onOpenChange }) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange !== undefined ? onOpenChange : setUncontrolledOpen;

  return (
    <AlertDialogContext.Provider value={{ open, setOpen }}>
      {children}
    </AlertDialogContext.Provider>
  );
};

const AlertDialogTrigger: React.FC<AlertDialogTriggerProps> = ({ children, asChild }) => {
  const context = React.useContext(AlertDialogContext);
  if (!context) {
    throw new Error('AlertDialogTrigger must be used within an AlertDialog');
  }
  const { setOpen } = context;

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, { onClick: () => setOpen(true) });
  }

  return <button onClick={() => setOpen(true)}>{children}</button>;
};

const AlertDialogContent: React.FC<AlertDialogContentProps> = ({ children, className }) => {
  const context = React.useContext(AlertDialogContext);
  if (!context) {
    throw new Error('AlertDialogContent must be used within an AlertDialog');
  }
  const { open, setOpen } = context;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-xl p-6 max-w-md w-full relative ${className || ''}`}>
        {children}
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

const AlertDialogHeader: React.FC<AlertDialogHeaderProps> = ({ className, ...props }) => (
  <div className={`flex flex-col space-y-2 text-center sm:text-left ${className || ''}`} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter: React.FC<AlertDialogFooterProps> = ({ className, ...props }) => (
  <div className={`flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 ${className || ''}`} {...props} />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle: React.FC<AlertDialogTitleProps> = ({ className, children, ...props }) => (
  <h3 className={`text-lg font-semibold ${className || ''}`} {...props}>
    {children}
  </h3>
);
AlertDialogTitle.displayName = 'AlertDialogTitle';

const AlertDialogDescription: React.FC<AlertDialogDescriptionProps> = ({ className, ...props }) => (
  <p className={`text-sm text-gray-500 ${className || ''}`} {...props} />
);
AlertDialogDescription.displayName = 'AlertDialogDescription';

const AlertDialogAction: React.FC<AlertDialogActionProps> = ({ className, ...props }) => {
  const context = React.useContext(AlertDialogContext);
  if (!context) {
    throw new Error('AlertDialogAction must be used within an AlertDialog');
  }
  const { setOpen } = context;
  return (
    <Button
      className={`bg-blue-600 text-white hover:bg-blue-700 ${className || ''}`}
      onClick={() => setOpen(false)}
      {...props}
    />
  );
};
AlertDialogAction.displayName = 'AlertDialogAction';

const AlertDialogCancel: React.FC<AlertDialogCancelProps> = ({ className, ...props }) => {
  const context = React.useContext(AlertDialogContext);
  if (!context) {
    throw new Error('AlertDialogCancel must be used within an AlertDialog');
  }
  const { setOpen } = context;
  return (
    <Button
      variant="outline"
      className={`mt-2 sm:mt-0 ${className || ''}`}
      onClick={() => setOpen(false)}
      {...props}
    />
  );
};
AlertDialogCancel.displayName = 'AlertDialogCancel';

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};